import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Download, 
  Share2, 
  Map, 
  Camera, 
  Settings,
  Plus,
  Trash2,
  Eye,
  Edit,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { MapContainer, TileLayer, Polygon, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const ProjectEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [polygon, setPolygon] = useState([]);
  const [surface, setSurface] = useState(0);
  const [nbPanneaux, setNbPanneaux] = useState(0);

  const isNewProject = id === 'new';

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: Eye },
    { id: 'mapping', name: 'Cartographie', icon: Map },
    { id: 'drone', name: 'Images Drone', icon: Camera },
    { id: 'calculation', name: 'Calculs', icon: Settings },
    { id: 'export', name: 'Export', icon: Download }
  ];

  const handleSave = () => {
    toast.success('Projet sauvegardé avec succès !');
    setIsEditing(false);
  };

  const handleExport = () => {
    toast.success('Export PDF en cours de génération...');
  };

  const handleAutoSizing = () => {
    toast.success('Dimensionnement automatique lancé !');
    // Ici tu pourras appeler une API ou une logique métier pour calculer la disposition
  };

  function PolygonDrawer({ onPolygonComplete }) {
    useMapEvents({
      click(e) {
        setPolygon((prev) => [...prev, [e.latlng.lat, e.latlng.lng]]);
      },
      dblclick() {
        if (polygon.length > 2) {
          onPolygonComplete(polygon);
        }
      }
    });
    return polygon.length > 0 ? <Polygon positions={polygon} pathOptions={{ color: 'orange' }} /> : null;
  }

  const handlePolygonComplete = (poly) => {
    // Calculer la surface fictive (en m²) et le nombre de panneaux (surface/1.7)
    // Ici, on fait un calcul simple (pas géodésique précis)
    const s = Math.abs(
      poly.reduce((acc, curr, i, arr) => {
        if (i === arr.length - 1) return acc;
        return acc + (curr[0] * arr[i + 1][1] - arr[i + 1][0] * curr[1]);
      }, 0) / 2
    ) * 111000 * 111000 / 1e6; // approx en m²
    setSurface(s.toFixed(1));
    setNbPanneaux(Math.floor(s / 1.7));
  };

  return (
    <div className="min-h-screen pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </motion.button>
            <div>
              <h1 className="text-3xl font-display font-bold text-white">
                {isNewProject ? 'Nouveau Projet' : 'Éditer le Projet'}
              </h1>
              <p className="text-white/70">
                {isNewProject ? 'Créez votre nouveau projet photovoltaïque' : 'Modifiez les paramètres du projet'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExport}
              className="button-secondary"
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              className="button-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              Sauvegarder
            </motion.button>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-effect rounded-2xl p-2 mb-8"
        >
          <div className="flex space-x-2">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.name}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-effect rounded-2xl p-8"
        >
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Informations Générales</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Nom du projet
                      </label>
                      <input
                        type="text"
                        defaultValue="Villa du Lac - Installation PV"
                        className="input-field w-full"
                        placeholder="Nom du projet"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Client
                      </label>
                      <input
                        type="text"
                        defaultValue="M. et Mme Martin"
                        className="input-field w-full"
                        placeholder="Nom du client"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Adresse
                      </label>
                      <input
                        type="text"
                        defaultValue="123 Chemin du Lac, 74000 Annecy"
                        className="input-field w-full"
                        placeholder="Adresse du projet"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Paramètres Techniques</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Puissance installée (kWc)
                      </label>
                      <input
                        type="number"
                        defaultValue="6.2"
                        className="input-field w-full"
                        placeholder="0.0"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Orientation (°)
                      </label>
                      <input
                        type="number"
                        defaultValue="180"
                        className="input-field w-full"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Inclinaison (°)
                      </label>
                      <input
                        type="number"
                        defaultValue="30"
                        className="input-field w-full"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Estimation de Production</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h4 className="text-white/70 text-sm mb-2">Production Annuelle</h4>
                    <p className="text-3xl font-bold text-white">7,200 kWh</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6">
                    <h4 className="text-white/70 text-sm mb-2">Économies CO₂</h4>
                    <p className="text-3xl font-bold text-white">2.8 tonnes</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6">
                    <h4 className="text-white/70 text-sm mb-2">ROI Estimé</h4>
                    <p className="text-3xl font-bold text-white">8.5 ans</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mapping' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Cartographie Interactive</h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="button-primary"
                  onClick={() => { setPolygon([]); setSurface(0); setNbPanneaux(0); }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Réinitialiser
                </motion.button>
              </div>
              <div className="aspect-video rounded-xl border-2 border-white/20 overflow-hidden">
                <MapContainer center={[48.858, 2.294]} zoom={18} style={{ height: '100%', width: '100%' }} doubleClickZoom={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <PolygonDrawer onPolygonComplete={handlePolygonComplete} />
                  {polygon.length > 0 && <Polygon positions={polygon} pathOptions={{ color: 'orange' }} />}
                </MapContainer>
              </div>
              {surface > 0 && (
                <div className="bg-white/10 rounded-xl p-4 text-white flex flex-col md:flex-row md:items-center md:space-x-8">
                  <div>Surface estimée : <span className="font-bold">{surface} m²</span></div>
                  <div>Nombre de panneaux (1.7m²) : <span className="font-bold">{nbPanneaux}</span></div>
                </div>
              )}
              <div className="flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="button-primary"
                  onClick={handleAutoSizing}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Dimensionner automatiquement
                </motion.button>
              </div>
            </div>
          )}

          {activeTab === 'drone' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Images Drone</h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="button-primary"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Importer des images
                </motion.button>
              </div>
              
              <div className="aspect-video bg-gradient-to-br from-green-400/20 to-solar-400/20 rounded-xl flex items-center justify-center border-2 border-dashed border-white/20">
                <div className="text-center">
                  <Camera className="h-16 w-16 text-white/50 mx-auto mb-4" />
                  <p className="text-white/70 mb-4">Import d'images drone</p>
                  <p className="text-white/50 text-sm">Glissez-déposez vos images ou cliquez pour sélectionner</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'calculation' && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Calculs Automatiques</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Disposition des Panneaux</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/70">Nombre de panneaux</span>
                      <span className="text-white font-semibold">16</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Surface utilisée</span>
                      <span className="text-white font-semibold">26.4 m²</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Rendement</span>
                      <span className="text-white font-semibold">85%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Optimisation</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/70">Espacement optimal</span>
                      <span className="text-white font-semibold">1.2 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Hauteur de montage</span>
                      <span className="text-white font-semibold">0.3 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Pertes ombrage</span>
                      <span className="text-white font-semibold">2.3%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'export' && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Export et Partage</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Export PDF</h4>
                  <p className="text-white/70 mb-4">
                    Générez un rapport PDF professionnel avec toutes les données du projet.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleExport}
                    className="button-primary w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Générer le PDF
                  </motion.button>
                </div>

                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Partage</h4>
                  <p className="text-white/70 mb-4">
                    Partagez votre projet avec vos clients ou collaborateurs.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="button-secondary w-full"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Partager le projet
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectEditor; 